import React from "react";
import { motion } from "framer-motion";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import {
  FileText,
  Mail,
  BarChart3,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

const WorkflowSteps: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const iconVariants = {
    hidden: {
      scale: 0,
      rotate: -180,
    },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.5,
        ease: "backOut",
      },
    },
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: {
        duration: 0.2,
      },
    },
  };

  const workflowSteps = [
    {
      title: t("workflow.steps.0.title"),
      description: t("workflow.steps.0.description"),
      icon: FileText,
      color: "bg-blue-500",
      delay: 0,
    },
    {
      title: t("workflow.steps.1.title"),
      description: t("workflow.steps.1.description"),
      icon: Mail,
      color: "bg-green-500",
      delay: 0.2,
    },
    {
      title: t("workflow.steps.2.title"),
      description: t("workflow.steps.2.description"),
      icon: BarChart3,
      color: "bg-purple-500",
      delay: 0.4,
    },
    {
      title: t("workflow.steps.3.title"),
      description: t("workflow.steps.3.description"),
      icon: CheckCircle,
      color: "bg-orange-500",
      delay: 0.6,
    },
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t("workflow.title")}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full"></div>
        </motion.div>

        {/* Workflow Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="relative"
        >
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-200 via-blue-200 to-orange-200 transform -translate-y-1/2 z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {workflowSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="relative group"
                >
                  {/* Step Card */}
                  <div className="bg-white rounded-2xl p-8 transition-all duration-300 border border-gray-100 relative z-10">
                    {/* Icon Container */}
                    <motion.div
                      variants={iconVariants}
                      whileHover="hover"
                      className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300`}
                    >
                      <IconComponent className="w-8 h-8 text-white" />
                    </motion.div>

                    {/* Step Number */}
                    {/* <div className="absolute -top-3 -right-3 w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div> */}

                    {/* Content */}
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors duration-300">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>

                    {/* Hover Effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                  </div>

                  {/* Arrow for mobile */}
                  {index < workflowSteps.length - 1 && (
                    <div className="lg:hidden flex justify-center mt-6">
                      <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: step.delay + 0.3 }}
                        className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center"
                      >
                        <ArrowRight className="w-4 h-4 text-gray-600" />
                      </motion.div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Progress Indicators for Desktop */}
          <div className="hidden lg:flex justify-between absolute top-1/2 left-0 right-0 transform -translate-y-1/2 z-20">
            {workflowSteps.map((_, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.2 + 0.5 }}
                className="w-4 h-4 bg-white border-4 border-purple-500 rounded-full"
              />
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-3 rounded-full">
            <span className="font-semibold">{t("actions.startNow")}</span>
            <ArrowRight className="w-4 h-4" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkflowSteps;
